import { useCallback, useMemo } from 'react';
import { useLocation } from '@reach/router';
import { useQueryFilter } from 'hooks/useQueryFilter';
import { useApikey } from 'hooks/useApikey';
import { useUser } from 'services/User';
import {
    CVEsInterface,
    FileResponseInterface,
    SandboxResponseInterface,
    VulnerabilityDataInterface,
    VulnerabilityResponseInterface,
} from 'services/interface';
import {
    axios,
    type Fetcher,
    type MutationFetcher,
    type SWRConfiguration,
    type SWRKey,
    useSWR,
    useSWRImmutable,
    useSWRInfinite,
    useSWRMutation,
} from 'services/swr';
import { getFileComplete } from 'utils/file/Format';
import { isSandboxInProgress, sandboxRefreshInterval } from 'utils/sandbox/SandboxFunctions';

export const resolveResultFileSha1 = (fileData?: FileResponseInterface | null): string | undefined => {
    const nested = typeof fileData?.file_info?.sha1 === 'string' ? fileData.file_info.sha1.trim() : '';

    if (nested.length > 0) {
        return nested;
    }

    const root = typeof fileData?.sha1 === 'string' ? fileData.sha1.trim() : '';

    return root.length > 0 ? root : undefined;
};

export const useFileData = ({ dataId }: { dataId?: string | null } = {}) => {
    const { pathname } = useLocation();
    const segments = pathname.split('/');
    const type = segments[2];
    const id = segments[3];

    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });

    const resultsKey = useMemo<SWRKey>(() => {
        if (!apikey || dataId === null) {
            return null;
        }

        const headers = { apikey };

        if (typeof dataId === 'string') {
            return { url: `/v4/file/${dataId}`, headers };
        }

        if (id && type === 'file') {
            return { url: `/v4/file/${id}`, headers };
        }

        if (id && type === 'hash') {
            return { url: `/v4/hash/${id}`, headers };
        }

        return null;
    }, [id, type, dataId, apikey]);

    const fileRefreshInterval = (data?: FileResponseInterface) => {
        const isScanComplete = getFileComplete(data);

        if (type === 'hash') {
            return 0;
        }

        if (!isScanComplete) {
            return 3000;
        }

        // Mirrors the running conditions of `sandboxRefreshInterval` — not the whole function: its
        // `final_verdict` branch returns 0, and the embedded shape carries `final_verdict` alongside a
        // still-running `scan_result_i`, which would freeze the page on "In Progress".
        if (isSandboxInProgress(data?.sandbox_results)) {
            return 10000;
        }

        return 0;
    };

    // An explicit `dataId` fetch is only the archive-parent context for the sidebar (ResultsLayout /
    // ArchiveMenu): fetch it once and serve it from cache. Letting it share the page's polling config
    // re-requested the parent on every child navigation and on every 3s/10s tick of the child page
    // (MDCI-931) — only the file being viewed should poll. Ceiling: a sibling list fetched
    // mid-extraction stays stale until reload — poll here again if that ever matters.
    const isContextFetch = typeof dataId === 'string';

    return useSWR<FileResponseInterface>(resultsKey, {
        refreshInterval: isContextFetch ? 0 : fileRefreshInterval,
        revalidateIfStale: !isContextFetch,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

/**
 * A `lastSandboxId` query param may only win when it belongs to the file being viewed (its current
 * `sandbox_id` or one of its `last_sandbox_id` re-scans). A stale id carried over from another page —
 * e.g. the parent archive's when navigating to an extracted child — would otherwise load that file's
 * report instead of the child's own `sandbox_id` (MDCI-931).
 */
export const resolveSandboxId = (
    fileData?: FileResponseInterface | null,
    lastSandboxId?: string,
): string | undefined => {
    const ownsLastSandboxId =
        Boolean(lastSandboxId) &&
        (lastSandboxId === fileData?.sandbox_id ||
            Boolean(fileData?.last_sandbox_id?.some((entry) => entry.sandbox_id === lastSandboxId)));

    return ownsLastSandboxId ? lastSandboxId : fileData?.sandbox_id;
};

export const useSandboxData = () => {
    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });

    const { data: fileData } = useFileData();
    const { lastSandboxId } = useQueryFilter();
    const sandboxId = resolveSandboxId(fileData, lastSandboxId);
    // An empty / placeholder `sandbox_results` must not disable the `/v4/sandbox` fallback: without a
    // status or a report location there is nothing to render, and the file poll would stop too.
    const hasEmbeddedResults =
        fileData?.sandbox_results?.scan_result_i != null || Boolean(fileData?.sandbox_results?.store_at);
    const embeddedSandboxResults =
        hasEmbeddedResults && sandboxId && sandboxId === fileData?.sandbox_id
            ? fileData?.sandbox_results
            : undefined;
    const sandboxResponseKey: SWRKey =
        !embeddedSandboxResults && sandboxId && apikey
            ? { url: `/v4/sandbox/${sandboxId}`, headers: { apikey } }
            : null;

    const sandboxResponse = useSWR<SandboxResponseInterface>(sandboxResponseKey, {
        refreshInterval: sandboxRefreshInterval,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    if (embeddedSandboxResults && sandboxId) {
        const embeddedData = { ...embeddedSandboxResults, sandbox_id: sandboxId };
        // A re-scan keeps the PREVIOUS run's snapshot embedded in the file response: while the file is
        // still analyzing, that snapshot looks finished but its report link can already be dead (400).
        // Withhold `store_at` until the current scan completes so useSandboxReport doesn't fetch a
        // stale report (MDCI-931).
        if (!getFileComplete(fileData)) {
            delete embeddedData.store_at;
        }
        return { ...sandboxResponse, data: embeddedData };
    }

    return sandboxResponse;
};

export const useSandboxReport = () => {
    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });
    const { data: sandboxData } = useSandboxData();
    // `store_at` is already populated while the analysis runs, and the report is fetched immutably —
    // fetching early would cache a partial report under a key that never changes.
    const sandboxDataKey: SWRKey =
        sandboxData?.store_at && apikey && !isSandboxInProgress(sandboxData)
            ? { url: sandboxData.store_at, headers: { apikey } }
            : null;

    // A report link either resolves or is stale (4xx) — retrying the same signed URL can't heal it,
    // it only spams the platform. A new report arrives as a new `store_at`, i.e. a new SWR key.
    return useSWRImmutable(sandboxDataKey, { shouldRetryOnError: false });
};

// IP / domain / URL results (Analyze IP-Domain-URL flow). Unlike useSandboxData / useSandboxReport
// above — which serve the authenticated file-scan flow and gate on an integration apikey — these
// results pages are public-capable (they render for logged-out users) and take the sandboxId from
// the URL-scan context, so the SWR keys are apikey-optional and the middleware supplies auth.
export const useIpDomainResultsData = (resultsUrl: string, sandboxId: string | undefined) => {
    const resultsKey: SWRKey = resultsUrl ? { url: resultsUrl } : null;
    const {
        data: results,
        isLoading: isLoadingResults,
        isValidating: isValidatingResults,
        error: resultsError,
    } = useSWRImmutable(resultsKey);

    const sandboxResponseKey: SWRKey = sandboxId ? { url: `/v4/sandbox/${sandboxId}` } : null;
    const sandboxResponseConfig: SWRConfiguration = {
        refreshInterval: sandboxRefreshInterval,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    };
    const { data: sandboxResponse } = useSWR<SandboxResponseInterface>(sandboxResponseKey, sandboxResponseConfig);

    const sandboxDataKey: SWRKey = sandboxResponse?.store_at ? { url: sandboxResponse.store_at } : null;
    const { data: sandboxData, error: sandboxError } = useSWR(sandboxDataKey, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    return {
        results,
        isLoadingResults,
        isValidatingResults,
        resultsError,
        sandboxResponse,
        sandboxData,
        sandboxError,
    };
};

export const VULNERABILITY_LIMIT = 10;

export interface HashVulnerabilityRow {
    cve: string;
    severity: string;
}

export const parseHashVulnerabilityResponse = (raw: unknown): HashVulnerabilityRow[] => {
    if (typeof raw !== 'object' || raw === null) {
        return [];
    }

    const record = raw as Record<string, unknown>;
    const cvesUnknown = record.cves;
    const vulnerabilityUnknown = record.vulnerability;

    if (!Array.isArray(cvesUnknown) || cvesUnknown.length === 0) {
        return [];
    }

    const severity =
        typeof vulnerabilityUnknown === 'object' &&
        vulnerabilityUnknown !== null &&
        typeof (vulnerabilityUnknown as Record<string, unknown>).severity === 'string'
            ? ((vulnerabilityUnknown as Record<string, unknown>).severity as string)
            : '';

    return cvesUnknown
        .map((cveUnknown) => (typeof cveUnknown === 'string' ? cveUnknown.trim() : ''))
        .filter((cve) => cve.length > 0)
        .map((cve) => ({ cve, severity }));
};

export const useVulnerabilityData = () => {
    const { data: fileData, isLoading: fileDataLoading } = useFileData();
    const hasVulnerability = fileData?.additional_info?.includes('vulnerability');
    const fileHash = resolveResultFileSha1(fileData);
    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });

    const vulnerabilityResponseKey: SWRKey =
        hasVulnerability && typeof fileHash === 'string' && fileHash.length > 0 && apikey
            ? { url: `/v4/hash/${fileHash}/vulnerability`, headers: { apikey } }
            : null;

    const {
        data: vulnerabilityResponse,
        isLoading: responseLoading,
        isValidating: responseValidating,
    } = useSWRImmutable<VulnerabilityResponseInterface>(vulnerabilityResponseKey, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
        revalidateIfStale: false,
    });

    const getVulnerabilityDataKey = useCallback(
        (index: number, prevPageData: VulnerabilityDataInterface | null) => {
            const pageLength = prevPageData?.data?.length ?? 0;
            const offset = index === 0 ? pageLength : index * pageLength;

            if (!(hasVulnerability && typeof fileHash === 'string' && fileHash.length > 0)) {
                return null;
            }

            if (index > 0 && pageLength < VULNERABILITY_LIMIT) {
                return null;
            }

            if (!apikey) return null;
            return {
                url: `/v4/vulnerability/${fileHash}?limit=${VULNERABILITY_LIMIT}&offset=${offset}`,
                headers: { apikey },
            };
        },
        [fileHash, hasVulnerability, apikey],
    );

    const {
        data: vulnerabilityDataList,
        setSize,
        isLoading: pagesLoading,
        isValidating: pagesValidating,
    } = useSWRInfinite<VulnerabilityDataInterface>(getVulnerabilityDataKey, {
        revalidateFirstPage: false,
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const paginatedData = useMemo(
        () =>
            (vulnerabilityDataList ?? []).reduce((accumulator: CVEsInterface[], value) => {
                if (value?.data) {
                    return [...accumulator, ...value.data];
                }

                return accumulator;
            }, []),
        [vulnerabilityDataList],
    );

    const lastVulnerabilityDataPage = vulnerabilityDataList?.at(-1);
    const hasLoadMore = Number(lastVulnerabilityDataPage?.data?.length) === VULNERABILITY_LIMIT;

    const data = useMemo<CVEsInterface[]>(() => {
        if (paginatedData.length > 0) {
            return paginatedData;
        }

        return parseHashVulnerabilityResponse(vulnerabilityResponse).map(
            (row) => ({ cve: row.cve, severity: row.severity }) as CVEsInterface,
        );
    }, [paginatedData, vulnerabilityResponse]);

    const totalCveCount = vulnerabilityResponse?.cves?.length ?? data.length;

    const loadMore = useCallback(() => {
        void setSize((currentSize) => currentSize + 1);
    }, [setSize]);

    return {
        data,
        hasLoadMore,
        isLoading: fileDataLoading || responseLoading || (pagesLoading && data.length === 0),
        isValidating: responseValidating || pagesValidating,
        loadMore,
        setSize,
        totalCveCount,
        vulnerabilityResponse,
    };
};

export const useHashScanHistory = () => {
    const { data: fileData } = useFileData();
    const fileHash = resolveResultFileSha1(fileData);
    const isScanComplete = getFileComplete(fileData);

    const { serviceId } = useQueryFilter();
    const { apikey } = useApikey({ serviceId });
    const key: SWRKey =
        isScanComplete && typeof fileHash === 'string' && fileHash.length > 0 && apikey
            ? { url: `/v4/hash/${fileHash}/scanhistory`, headers: { apikey } }
            : null;

    return useSWRImmutable<unknown>(key, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

export const useFileComments = () => {
    const { data: fileData } = useFileData();
    const fileHash = resolveResultFileSha1(fileData);

    const key: SWRKey = typeof fileHash === 'string' && fileHash.length > 0 ? { url: `/v4/comment/${fileHash}` } : null;

    return useSWR<unknown>(key, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });
};

const postCommunityFileCommentFetcher: MutationFetcher<unknown, SWRKey, { comment: string }> = async (
    { url, ...config },
    { arg },
) => {
    const response = await axios.post(url, arg, config);
    return response.data;
};

export const usePostCommunityFileComment = () => {
    const { data: fileData } = useFileData();
    const hash = resolveResultFileSha1(fileData);
    const key: SWRKey = { url: `/v4/comment/${hash}` };

    return useSWRMutation(key, postCommunityFileCommentFetcher, { throwOnError: false });
};

export type HashUserVote = 'up' | 'down' | null;

const handleHashUserVote: Fetcher<HashUserVote, SWRKey> = async ({ url, ...config }) => {
    const response = await axios.get(url, { ...config });
    const userVoteResponse = response.data;
    const userVoteData = Object.values(userVoteResponse)[0];

    if (userVoteData === 'upvote') {
        return 'up';
    }

    if (userVoteData === 'downvote') {
        return 'down';
    }

    return null;
};

const hashVotesFetcher: MutationFetcher<unknown, SWRKey> = async (config) => {
    const response = await axios(config);
    return response.data;
};

export const useHashCommunityVotes = () => {
    const { user } = useUser();
    const { data: fileData, mutate: mutateFileData, isLoading: fileDataLoading } = useFileData();
    const hash = resolveResultFileSha1(fileData);
    const isLoggedIn = Boolean(user?.isLoggedIn);
    const canVote = isLoggedIn && user?.org_role !== 'analyst';
    const apikey = user?.portal_api_key;

    const userVoteKey: SWRKey = hash && isLoggedIn ? { url: `/v4/hash/${hash}/vote` } : null;
    const {
        data: userVote,
        mutate: mutateUserVote,
        isValidating: userVoteValidating,
    } = useSWRImmutable<HashUserVote>(userVoteKey, handleHashUserVote, {
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
    });

    const deleteVoteKey: SWRKey = { url: `/v4/hash/${hash}/vote`, method: 'DELETE' };
    const { trigger: deleteVote, isMutating: deleteIsMutating } = useSWRMutation(deleteVoteKey, hashVotesFetcher);

    const upVoteKey: SWRKey = { url: `/v4/hash/${hash}/upvote`, method: 'PUT', data: {} };
    const { trigger: upVote, isMutating: upIsMutating } = useSWRMutation(upVoteKey, hashVotesFetcher);

    const downVoteKey: SWRKey = { url: `/v4/hash/${hash}/downvote`, method: 'PUT', data: {} };
    const { trigger: downVote, isMutating: downIsMutating } = useSWRMutation(downVoteKey, hashVotesFetcher);

    const isVoteDisabled =
        fileDataLoading ||
        userVoteValidating ||
        deleteIsMutating ||
        upIsMutating ||
        downIsMutating ||
        !canVote ||
        !apikey;

    const applyVote = useCallback(
        async (direction: 'up' | 'down') => {
            if (!hash || !canVote) {
                return;
            }

            if (userVote === direction) {
                await deleteVote();
            } else if (direction === 'up') {
                await upVote();
            } else {
                await downVote();
            }

            await Promise.all([mutateUserVote(), mutateFileData()]);
        },
        [canVote, deleteVote, downVote, hash, mutateFileData, mutateUserVote, upVote, userVote],
    );

    return {
        applyVote,
        downCount: fileData?.votes?.down ?? 0,
        isVoteDisabled,
        upCount: fileData?.votes?.up ?? 0,
        userVote: userVote ?? null,
    };
};
